/**
 * @fileoverview Special Piece Skill Handler
 *
 * ハンドラ契約:
 * - showEffect(def, target): エフェクト演出（オプション）
 * - execute(def, target): 実行（必須）
 */

import { ROWS, COLS } from '../../../core/constants.js';
import { SKILL_CONSTANTS } from '../../../presentation/constants.js';
import { MinimalErrorTracker } from '../../../shared/log.js';
import { runCascadeFromRemovals } from '../../cascade.js';
import { getGameBoard } from '../../game-controls.js';

/**
 * エフェクト演出（5x5範囲プレビュー）
 * @param {Object} def - スキル定義
 * @param {Object} target - ターゲット座標 { row, col }
 * @returns {Promise<void>}
 */
export async function showEffect(def, target) {
  const previewEls = [];
  const { row, col } = target;

  // 5x5範囲（中心から±2）のセルにプレビュークラス付与
  for (let r = Math.max(0, row - 2); r <= Math.min(ROWS - 1, row + 2); r++) {
    for (let c = Math.max(0, col - 2); c <= Math.min(COLS - 1, col + 2); c++) {
      const cellEl = getCellElement(r, c);
      if (cellEl) {
        // マンハッタン距離に応じた遅延
        const dist = Math.abs(r - row) + Math.abs(c - col);
        const ratio = Math.min(dist / SKILL_CONSTANTS.SKILL_RADIUS, 1);
        const delayMs = Math.floor(
          SKILL_CONSTANTS.PREVIEW_DELAY_BASE + ratio * SKILL_CONSTANTS.PREVIEW_DELAY_RATIO,
        );

        cellEl.style.setProperty('--skill-preview-delay', `${delayMs}ms`);
        cellEl.classList.add('skill-preview');
        previewEls.push(cellEl);
      }
    }
  }

  // プレビュー完了まで待機
  await new Promise((resolve) => {
    setTimeout(() => {
      // クリーンアップ
      previewEls.forEach((el) => {
        el.classList.remove('skill-preview');
        el.style.removeProperty('--skill-preview-delay');
      });
      resolve();
    }, SKILL_CONSTANTS.PREVIEW_DURATION);
  });
}

/**
 * 実行（特殊ピース生成 → カスケード）
 * @param {Object} def - スキル定義
 * @param {Object} target - ターゲット座標 { row, col }
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function execute(def, target) {
  try {
    // ターゲット検証
    if (!target || typeof target.row !== 'number' || typeof target.col !== 'number') {
      return { ok: false, reason: 'invalid_target' };
    }
    if (target.row < 0 || target.row >= ROWS || target.col < 0 || target.col >= COLS) {
      return { ok: false, reason: 'invalid_target' };
    }

    const board = getGameBoard();
    const { row, col } = target;

    // 対象セルのピース取得
    const originalPiece = (() => {
      try {
        return board.getPiece(row, col);
      } catch (error) {
        MinimalErrorTracker.info('SKILL', 'Board piece access failed', { row, col });
        return null;
      }
    })();

    // スキルピース生成
    const skillPiece = {
      ...(originalPiece || { color: 'red' }),
      position: [row, col],
      specialType: def.specialType,
    };

    // カスケード処理を実行
    await runCascadeFromRemovals(board, [skillPiece]);

    return { ok: true };
  } catch (error) {
    MinimalErrorTracker.error('SKILL', 'Special skill execution failed', error);
    return { ok: false, reason: 'execution_error', error };
  }
}

/**
 * セル要素の取得
 * @private
 * @param {number} row - 行
 * @param {number} col - 列
 * @returns {HTMLElement|null}
 */
function getCellElement(row, col) {
  if (!Number.isInteger(row) || !Number.isInteger(col)) return null;
  if (row < 0 || row >= ROWS || col < 0 || col >= COLS) return null;

  const element = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
  return element instanceof HTMLElement ? element : null;
}
